import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { MdClose } from 'react-icons/md';
import Overflow from 'react-overflow-indicator';
import styled, { css } from 'styled-components';
import { FormattedMessage, useIntl, defineMessages } from '@techstyle/react-intl';
import Button from '../Button';
import Drawer from '../Drawer';
import FilterTags, { AggregationFilterTags, CategoryFilterTags } from '../FilterTagsCustomFilters';
import { useProductBrowser } from '../ProductBrowserContext';
import { ProductFilters } from '../ProductBrowserCustomFilters';
import ProductFilterContext, { useProductFilters } from '../ProductFilterContextCustomFilters';
import { getTotalFilterCountCustomFilters } from '../utils/category';
import { openCustomFilterDrawers } from '../utils/filters';
var messages = defineMessages({
  filterDrawerTitle: {
    id: 'site_product_grid.filter_drawer_title',
    defaultMessage: 'Product Filters'
  }
});
var FiltersOverflow = styled(Overflow).withConfig({
  displayName: "FilterDrawerCustomFilters__FiltersOverflow",
  componentId: "sc-m0eydv-0"
})(["\n  flex: 1 1 auto;\n  overflow: auto;\n"]);
var AllFiltersWrapper = styled.div.withConfig({
  displayName: "FilterDrawerCustomFilters__AllFiltersWrapper",
  componentId: "sc-m0eydv-1"
})(["\n  padding-bottom: 16px;\n"]);
var ApplyFiltersWrapper = styled.div.withConfig({
  displayName: "FilterDrawerCustomFilters__ApplyFiltersWrapper",
  componentId: "sc-m0eydv-2"
})(["\n  position: relative;\n  flex: 0 0 auto;\n  padding: 16px;\n  padding-top: 0;\n  display: flex;\n  justify-content: center;\n  align-items: center;\n"]);
var ApplyFilters = styled(Button).attrs({
  'data-autotag': 'filter-apply',
  variant: 'applyFilters'
}).withConfig({
  displayName: "FilterDrawerCustomFilters__ApplyFilters",
  componentId: "sc-m0eydv-3"
})(["\n  min-width: 248px;\n  height: 48px;\n  border: none;\n  color: #fff;\n  background-color: #333;\n  border-radius: 4px;\n  font-size: 16px;\n  font-weight: bold;\n  letter-spacing: ", "em;\n  text-align: center;\n\n  :disabled {\n    color: #999;\n    background-color: #ddd;\n  }\n\n  ", ";\n"], -0.4 / 16, _ref => {
  var buttonStyle = _ref.buttonStyle;
  return buttonStyle;
});

var getDialogStyle = additionalStyles => css(["\n  background-color: #ffffff;\n  box-shadow: -4px 0 8px 0 rgba(0, 0, 0, 0.04), inset 1px 0 0 0 #eeeeee;\n  width: 280px;\n  max-width: calc(100vw - 44px);\n  overflow: visible;\n\n  ", "\n"], additionalStyles);

var OverflowFade = styled.div.withConfig({
  displayName: "FilterDrawerCustomFilters__OverflowFade",
  componentId: "sc-m0eydv-4"
})(["\n  box-sizing: content-box;\n  position: absolute;\n  bottom: 0;\n  left: 0;\n  padding-top: 16px;\n  width: 100%;\n  height: 100%;\n  background-image: linear-gradient(\n    to bottom,\n    rgba(255, 255, 255, 0),\n    #ffffff 20%\n  );\n  pointer-events: none;\n"]); // Needs a z-index of 1 because the shadow won't display over the checkmark areas

var OverflowShadow = styled.div.withConfig({
  displayName: "FilterDrawerCustomFilters__OverflowShadow",
  componentId: "sc-m0eydv-5"
})(["\n  position: absolute;\n  top: 0;\n  bottom: 0;\n  left: 0;\n  right: 0;\n  box-shadow: 0px 2px 4px rgba(0, 0, 0, 0.08);\n  pointer-events: none;\n  z-index: 1;\n"]);

var getOverlayStyle = additionalStyles => css(["\n  background-color: rgba(0, 0, 0, 0.5);\n  will-change: background-color;\n\n  ", "\n"], additionalStyles);

var CloseButton = styled(Button).attrs({
  variant: 'closeFilterDrawer'
}).withConfig({
  displayName: "FilterDrawerCustomFilters__CloseButton",
  componentId: "sc-m0eydv-6"
})(["\n  position: absolute;\n  right: 100%;\n  width: 44px;\n  height: auto;\n  background-color: transparent;\n  border: none;\n  font-size: 12px;\n  font-family: inherit;\n  line-height: 1;\n  letter-spacing: ", "em;\n  color: #ffffff;\n  text-align: center;\n  padding: 10px 4px;\n\n  ", ";\n"], -0.3 / 12, _ref2 => {
  var closeButtonStyle = _ref2.closeButtonStyle;
  return closeButtonStyle;
});
var TimesIcon = styled(MdClose).withConfig({
  displayName: "FilterDrawerCustomFilters__TimesIcon",
  componentId: "sc-m0eydv-7"
})(["\n  font-size: 16px;\n  color: inherit;\n  vertical-align: middle;\n"]);
var FilterTagsWrapper = styled.div.withConfig({
  displayName: "FilterDrawerCustomFilters__FilterTagsWrapper",
  componentId: "sc-m0eydv-8"
})(["\n  position: relative;\n  flex: 0 0 auto;\n  padding: 13px 16px;\n\n  ", ";\n"], _ref3 => {
  var wrapperStyle = _ref3.wrapperStyle;
  return wrapperStyle;
});
var NoFilterAppliedMessage = styled.p.withConfig({
  displayName: "FilterDrawerCustomFilters__NoFilterAppliedMessage",
  componentId: "sc-m0eydv-9"
})(["\n  font-size: 16px;\n  line-height: ", ";\n  letter-spacing: ", "em;\n  color: #999999;\n\n  ", ";\n"], 22 / 16, -0.4 / 16, _ref4 => {
  var noFiltersAppliedStyle = _ref4.noFiltersAppliedStyle;
  return noFiltersAppliedStyle;
});

function ApplyFiltersButton(_ref5) {
  var buttonStyle = _ref5.buttonStyle,
      children = _ref5.children,
      onClickFromProps = _ref5.onClickCallback,
      rest = _objectWithoutProperties(_ref5, ["buttonStyle", "children", "onClickCallback"]);

  var _useProductFilters = useProductFilters(),
      applySelectedFilters = _useProductFilters.applySelectedFilters,
      isDirty = _useProductFilters.isDirty,
      selectedFilters = _useProductFilters.selectedFilters;

  var onApplyFilters = useCallback(() => {
    if (onClickFromProps) {
      onClickFromProps(selectedFilters);
      applySelectedFilters();
    } else {
      applySelectedFilters();
    }
  }, [applySelectedFilters, onClickFromProps, selectedFilters]);
  return /*#__PURE__*/React.createElement(ApplyFilters, _extends({
    onClick: onApplyFilters,
    disabled: !isDirty,
    buttonStyle: buttonStyle
  }, rest), children || /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "site_product_grid.apply_filters",
    defaultMessage: "Apply Filters"
  }));
}

ApplyFiltersButton.propTypes = {
  buttonStyle: PropTypes.any,
  children: PropTypes.node,

  /**
   * An additional `onClick` that is only called here, allowing the
   * existing onClick to be overridded for backward compatibility.
   */
  onClickCallback: PropTypes.func
}; // We will always get categoryFilter from FilterDrawer either from props or from ProductBrowserContext

var FilterDrawerTags = _ref6 => {
  var _ref6$applyOnClearAll = _ref6.applyOnClearAll,
      applyOnClearAll = _ref6$applyOnClearAll === void 0 ? false : _ref6$applyOnClearAll,
      _ref6$as = _ref6.as,
      ElementType = _ref6$as === void 0 ? FilterTags : _ref6$as,
      baseFiltersFromProps = _ref6.baseFilters,
      categoryFilterFromProps = _ref6.categoryFilter,
      noFiltersAppliedStyle = _ref6.noFiltersAppliedStyle,
      wrapperStyle = _ref6.wrapperStyle,
      rest = _objectWithoutProperties(_ref6, ["applyOnClearAll", "as", "baseFilters", "categoryFilter", "noFiltersAppliedStyle", "wrapperStyle"]);

  var _useProductFilters2 = useProductFilters(),
      applyFilters = _useProductFilters2.applyFilters,
      filterSettings = _useProductFilters2.filterSettings,
      selectedFilters = _useProductFilters2.selectedFilters,
      setFilterSettings = _useProductFilters2.setFilterSettings,
      setSelectedFilters = _useProductFilters2.setSelectedFilters,
      toggleCollapsibleFilterIsOpen = _useProductFilters2.toggleCollapsibleFilterIsOpen;

  var _useProductBrowser = useProductBrowser(),
      openFilterDrawersOnClearAll = _useProductBrowser.openFilterDrawersOnClearAll,
      productBrowserCtx = _objectWithoutProperties(_useProductBrowser, ["openFilterDrawersOnClearAll"]);

  var baseFilters = baseFiltersFromProps;
  var categoryFilter = categoryFilterFromProps;

  if (productBrowserCtx) {
    var baseFiltersFromCtx = productBrowserCtx.baseFilters,
        categoryFilterFromCtx = productBrowserCtx.categoryFilter;

    if (!baseFiltersFromProps) {
      baseFilters = baseFiltersFromCtx;
    }

    if (!categoryFilterFromProps) {
      categoryFilter = categoryFilterFromCtx;
    }
  } // Check if any of the filterSettings have a base value that we'll
  // need to reset them back to.


  var shouldClearFilterSettings = filterSettings.some(_ref7 => {
    var getBaseValue = _ref7.getBaseValue;
    return typeof getBaseValue === 'function';
  });
  var clearAllFilters = useCallback(() => {
    var baseFilterSettings = filterSettings;

    if (shouldClearFilterSettings) {
      baseFilterSettings = filterSettings.map(filterSetting => typeof filterSetting.getBaseValue === 'function' ? _objectSpread(_objectSpread({}, filterSetting), filterSetting.getBaseValue({
        filterSetting,
        selectedFilters
      })) : filterSetting);
    }

    if (applyOnClearAll) {
      applyFilters({
        newFilters: baseFilters,
        newFilterSettings: baseFilterSettings
      });
    } else {
      setSelectedFilters(baseFilters);
      setFilterSettings(baseFilterSettings);

      if (openFilterDrawersOnClearAll) {
        // Open all filter drawers;
        openCustomFilterDrawers(toggleCollapsibleFilterIsOpen, baseFilterSettings);
      } else {
        // Close all filter drawers;
        toggleCollapsibleFilterIsOpen({
          type: 'reset'
        });
      }
    }
  }, [applyFilters, applyOnClearAll, baseFilters, filterSettings, openFilterDrawersOnClearAll, selectedFilters, setFilterSettings, setSelectedFilters, shouldClearFilterSettings, toggleCollapsibleFilterIsOpen]);
  var totalSelectedFilterCount = getTotalFilterCountCustomFilters(filterSettings, selectedFilters);
  return /*#__PURE__*/React.createElement(FilterTagsWrapper, {
    wrapperStyle: wrapperStyle
  }, totalSelectedFilterCount > 0 ? /*#__PURE__*/React.createElement(FilterTags, _extends({
    onClearAll: clearAllFilters
  }, rest), categoryFilter && /*#__PURE__*/React.createElement(CategoryFilterTags, _extends({
    categoryFilter: categoryFilter,
    selectedFilters: selectedFilters
  }, rest)), filterSettings.map(filterSetting => {
    var _filterSetting$Filter = filterSetting.FilterTagComponent,
        FilterTagComponent = _filterSetting$Filter === void 0 ? AggregationFilterTags : _filterSetting$Filter,
        field = filterSetting.field;
    return /*#__PURE__*/React.createElement(React.Fragment, {
      key: field
    }, /*#__PURE__*/React.createElement(FilterTagComponent, {
      filterSetting: filterSetting,
      selectedFilters: selectedFilters
    }));
  })) : /*#__PURE__*/React.createElement(NoFilterAppliedMessage, {
    noFiltersAppliedStyle: noFiltersAppliedStyle
  }, /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "site_product_grid.no_filter_applied",
    defaultMessage: "No filters applied"
  })), /*#__PURE__*/React.createElement(Overflow.Indicator, {
    direction: "up"
  }, /*#__PURE__*/React.createElement(OverflowShadow, null)));
};

FilterDrawerTags.propTypes = {
  applyOnClearAll: PropTypes.bool,
  as: PropTypes.elementType,
  baseFilters: PropTypes.object,
  categoryFilter: PropTypes.object,
  filterSettings: PropTypes.object,
  noFiltersAppliedStyle: PropTypes.any,
  wrapperStyle: PropTypes.any
};

var OverflowFilterContent = _ref8 => {
  var children = _ref8.children,
      rest = _objectWithoutProperties(_ref8, ["children"]);

  return /*#__PURE__*/React.createElement(Overflow.Content, null, /*#__PURE__*/React.createElement(AllFiltersWrapper, rest, children));
};

OverflowFilterContent.propTypes = {
  children: PropTypes.node
};

var ApplyFiltersSection = _ref9 => {
  var buttonStyle = _ref9.buttonStyle,
      onClickFromProps = _ref9.onClickCallback,
      rest = _objectWithoutProperties(_ref9, ["buttonStyle", "onClickCallback"]);

  return /*#__PURE__*/React.createElement(ApplyFiltersWrapper, rest, /*#__PURE__*/React.createElement(Overflow.Indicator, {
    direction: "down"
  }, /*#__PURE__*/React.createElement(OverflowFade, null)), /*#__PURE__*/React.createElement(ApplyFiltersButton, {
    buttonStyle: buttonStyle,
    onClickCallback: onClickFromProps
  }));
};

ApplyFiltersSection.propTypes = {
  buttonStyle: PropTypes.any,
  onClickCallback: PropTypes.func
};
var FilterDrawer = /*#__PURE__*/React.forwardRef((_ref10, ref) => {
  var baseFiltersFromProps = _ref10.baseFilters,
      categoryFilterFromProps = _ref10.categoryFilter,
      children = _ref10.children,
      closeButtonStyle = _ref10.closeButtonStyle,
      _ref10$closeIcon = _ref10.closeIcon,
      closeIcon = _ref10$closeIcon === void 0 ? /*#__PURE__*/React.createElement(TimesIcon, null) : _ref10$closeIcon,
      colorSwatchesFromProps = _ref10.colorSwatches,
      dialogStyleFromProps = _ref10.dialogStyle,
      filtersFromProps = _ref10.filters,
      filterSettingsFromProps = _ref10.filterSettings,
      isOpenFromProps = _ref10.isOpen,
      onExitFromProps = _ref10.onExit,
      onSubmitFromProps = _ref10.onSubmit,
      overlayStyleFromProps = _ref10.overlayStyle,
      productFilterOptions = _ref10.productFilterOptions,
      renderCloseButton = _ref10.renderCloseButton,
      rest = _objectWithoutProperties(_ref10, ["baseFilters", "categoryFilter", "children", "closeButtonStyle", "closeIcon", "colorSwatches", "dialogStyle", "filters", "filterSettings", "isOpen", "onExit", "onSubmit", "overlayStyle", "productFilterOptions", "renderCloseButton"]);

  var productBrowserCtx = useProductBrowser();
  var dialogStyle = useMemo(() => getDialogStyle(dialogStyleFromProps), [dialogStyleFromProps]);
  var overlayStyle = useMemo(() => getOverlayStyle(overlayStyleFromProps), [overlayStyleFromProps]);
  var filterSettings = filterSettingsFromProps;
  var baseFilters = baseFiltersFromProps;
  var categoryFilter = categoryFilterFromProps;
  var isOpen = isOpenFromProps;

  if (productBrowserCtx) {
    var filterSettingsFromCtx = productBrowserCtx.filterSettings,
        baseFiltersFromCtx = productBrowserCtx.baseFilters,
        categoryFilterFromCtx = productBrowserCtx.categoryFilter,
        isOpenFromCtx = productBrowserCtx.isFilterDrawerOpen;

    if (filterSettingsFromProps == null) {
      filterSettings = filterSettingsFromCtx;
    }

    if (baseFiltersFromProps == null) {
      baseFilters = baseFiltersFromCtx;
    }

    if (categoryFilterFromProps == null) {
      categoryFilter = categoryFilterFromCtx;
    }

    if (isOpenFromProps == null) {
      isOpen = isOpenFromCtx;
    }
  }

  var _useProductFilters3 = useProductFilters(),
      appliedFilters = _useProductFilters3.appliedFilters;

  var intl = useIntl();
  var onExit = useCallback(() => {
    if (onExitFromProps) {
      onExitFromProps();
    } else if (productBrowserCtx) {
      productBrowserCtx.setFilterDrawerOpen(false);
    }
  }, [onExitFromProps, productBrowserCtx]);
  var handleSubmit = useCallback(_ref11 => {
    var newFilters = _ref11.newFilters,
        newFilterSettings = _ref11.newFilterSettings;

    if (onSubmitFromProps) {
      onSubmitFromProps(newFilters);
    } else if (productBrowserCtx) {
      productBrowserCtx.setFilters(newFilters);
      productBrowserCtx.setFilterSettings(newFilterSettings);
    }

    onExit();
  }, [onExit, onSubmitFromProps, productBrowserCtx]);
  return /*#__PURE__*/React.createElement(Drawer, _extends({
    "data-autotag": "filter-overlay",
    dialogStyle: dialogStyle,
    overlayStyle: overlayStyle,
    isOpen: isOpen,
    onExit: onExit,
    position: "right",
    ref: ref,
    title: intl.formatMessage(messages.filterDrawerTitle)
  }, rest), /*#__PURE__*/React.createElement(ProductFilterContext, {
    appliedFilters: appliedFilters,
    filterSettings: filterSettings,
    onApply: handleSubmit
  }, isOpen && (typeof renderCloseButton === 'function' ? renderCloseButton({
    onExit
  }) : /*#__PURE__*/React.createElement(CloseButton, {
    onClick: onExit,
    closeButtonStyle: closeButtonStyle
  }, /*#__PURE__*/React.createElement(React.Fragment, null, closeIcon, /*#__PURE__*/React.createElement("br", null), /*#__PURE__*/React.createElement(FormattedMessage, {
    id: "site_product_grid.close_filters",
    defaultMessage: "close"
  })))), children == null ? /*#__PURE__*/React.createElement(FiltersOverflow, null, /*#__PURE__*/React.createElement(FilterDrawerTags, {
    baseFilters: baseFilters,
    categoryFilter: categoryFilter
  }), /*#__PURE__*/React.createElement(OverflowFilterContent, null, /*#__PURE__*/React.createElement(ProductFilters, _extends({
    categoryFilter: categoryFilter,
    colorSwatches: colorSwatchesFromProps
  }, productFilterOptions))), /*#__PURE__*/React.createElement(ApplyFiltersWrapper, null, /*#__PURE__*/React.createElement(Overflow.Indicator, {
    direction: "down"
  }, /*#__PURE__*/React.createElement(OverflowFade, null)), /*#__PURE__*/React.createElement(ApplyFiltersButton, null))) : children));
});
FilterDrawer.displayName = 'FilterDrawer';
FilterDrawer.propTypes = {
  baseFilters: PropTypes.object,
  categoryFilter: PropTypes.shape({
    field: PropTypes.string,
    label: PropTypes.string,
    totalAggregationCount: PropTypes.number,
    items: PropTypes.array
  }),

  /**
   * children can either be a function or node. If children is a function,
   * you'll need to render FilterDrawer.Content with FilterDrawer.FilterTags,
   * FilterDrawer.Filters wrapping the filters you want to display, and
   * FilterDrawer.ApplyFilters as FilterDrawer.Content's children.
   * The function is passed the FilterDrawer's: aggregations, baseFilters, and categoryFilter.
   */
  children: PropTypes.node,
  closeButtonStyle: PropTypes.any,
  closeIcon: PropTypes.node,
  colorSwatches: PropTypes.object,
  dialogStyle: PropTypes.any,
  filterSettings: PropTypes.array,
  filters: PropTypes.object,
  isOpen: PropTypes.bool,

  /**
   * Callback function when the filters change.
   */
  onChange: PropTypes.func,
  onExit: PropTypes.func,

  /**
   * Callback function when user clicks "Apply Filters" button.
   */
  onSubmit: PropTypes.func,
  overlayStyle: PropTypes.any,

  /**
   * Options to pass as props to ProductFilters
   */
  productFilterOptions: PropTypes.object,

  /**
   * Function to display any custom close filter drawer button.
   */
  renderCloseButton: PropTypes.func
};
export default FilterDrawer;
FilterDrawer.Content = FiltersOverflow;
FilterDrawer.FilterTags = FilterDrawerTags;
FilterDrawer.Filters = OverflowFilterContent;
FilterDrawer.ApplyFilters = ApplyFiltersSection;